/**
 * RotaProfundaGuard.js
 *
 * Copiado de hay-hotspot/assets/reactweb/src/pages/Layout/RotaProfundaGuard.js (Fase 1.6).
 * Interpreta os segmentos extras de uma URL profunda (deep link) antes de montar o componente
 * real da rota (ver EntityGrid.abrirRegistroPorId).
 *
 * Diferença do molde: `render()` agora repassa navigate/location/searchparams/params/
 * configuracao pro componente da rota (o hay-hotspot original só passava `permissoes`) — sem
 * isso, `getRelacionados`/`buildRelacionados` recebiam `navigate` undefined e o clique em
 * qualquer item do menu "Relações" não fazia nada (bug reproduzido em Grupo->GrupoPermissao e
 * Usuario->UsuarioGrupo).
 */
import React, { Component } from 'react';
import { withRouter } from 'hay-core-react';
import { UtilService } from 'hay-core-react';
import { parseSegmentosProfundos, resolverContextoProfundo, codificarContexto, PARAM_CONTEXTO } from 'hay-core-react';

class RotaProfundaGuard extends Component {
  constructor(props) {
    super(props);
    this.state = { resolvendo: true };
  }

  componentDidMount() {
    this.resolver();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.params?.['*'] !== this.props.params?.['*']) {
      this.setState({ resolvendo: true }, () => this.resolver());
    }
  }

  async resolver() {
    const { cfg, controllerKeyAtual, rotaRegistry, navigate, searchparams, params, getRouter } = this.props;
    const splat = params?.['*'];
    const { proprio, relacao } = parseSegmentosProfundos(splat, rotaRegistry, controllerKeyAtual);

    if (!proprio && !relacao) {
      this.setState({ resolvendo: false });
      return;
    }

    UtilService.showLoading();
    try {
      await Promise.resolve();

      if (proprio) {
        navigate({ pathname: cfg.rota, search: { id: proprio.id, acao: proprio.acao } }, { replace: true });
        return;
      }

      const getFetcher = (key) => rotaRegistry[key]?.entidade;
      const contexto = await resolverContextoProfundo({
        rotaRegistry,
        controllerKeyAtual,
        idPai: relacao.idPai,
        relacaoChave: relacao.relacaoChave,
        idFilho: relacao.idFilho,
        acao: relacao.acao,
        searchParams: searchparams,
        getFetcher,
      });

      if (!contexto) {
        this.setState({ resolvendo: false });
        return;
      }

      const ctxCodificado = codificarContexto(contexto.ctx);
      const searchBase = ctxCodificado ? { [PARAM_CONTEXTO]: ctxCodificado } : {};

      if (!contexto.idFilho) {
        navigate({ pathname: contexto.relCfg.rota, search: searchBase }, { replace: true });
        return;
      }

      const queryBase = new URLSearchParams(searchBase).toString();
      const router = getRouter?.();
      if (router) {
        await router.navigate(
          { pathname: contexto.relCfg.rota, search: queryBase },
          { replace: true }
        );
      }

      navigate(
        { pathname: contexto.relCfg.rota, search: { ...searchBase, id: contexto.idFilho, acao: contexto.acaoAlvo } },
        { replace: false }
      );
    } finally {
      UtilService.hideLoading();
    }
  }

  render() {
    if (this.state.resolvendo) return null;
    const { cfg, permissoes, navigate, location, searchparams, params, configuracao } = this.props;
    const Componente = cfg.componente;
    return (
      <Componente
        permissoes={permissoes}
        navigate={navigate}
        location={location}
        searchparams={searchparams}
        params={params}
        configuracao={configuracao}
      />
    );
  }
}

export default withRouter(RotaProfundaGuard);
